import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import Button from '../../shared/components/ui/Button';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import {
  getCampaignMediaLibrary,
  getLeadsByStatusAndProject,
  getWhatsAppFlowById,
  getWhatsAppFlowPlaceholders,
  sendWhatsAppFlow,
  submitWhatsAppFlowTemplate,
  updateWhatsAppFlow,
  uploadFilesToS3
} from '../services/api/implementation/impl';
import Modal from '../../shared/components/ui/Modal';
import {
  Braces,
  ChevronLeft,
  ChevronDown,
  Eye,
  FileText,
  Image,
  Pencil,
  Paperclip,
  Play,
  Plus,
  Save,
  Search,
  Send,
  Trash2,
  Upload,
  Users,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  WA_SEND_CHUNK_SIZE,
  chunkArray,
  formatBulkSendToast,
  summarizeChunkResults
} from '../utils/bulkWhatsAppSendUtils';

const ADD_STEP_TABS = [
  { id: 'text_box', label: 'Text box' },
  { id: 'attach_files', label: 'Attach files' },
  { id: 'question', label: 'Question' },
  { id: 'end_flow', label: 'Message & end the flow' }
];

const PLACEHOLDERS_TAB = { id: 'placeholders', label: 'Placeholders' };

const FIRST_STEP_TABS = [
  { id: 'text_box', label: 'Message' },
  { id: 'question', label: 'Question' }
];

const MAX_TEMPLATE_BUTTONS = 3;
const MAX_BUTTON_LABEL_LENGTH = 20;
/** UI selection cap; API sends are chunked in batches of WA_SEND_CHUNK_SIZE. */
export const MAX_FLOW_SEND_RECIPIENTS = 500;

const PAGE_BG = '#F8F9FA';
/** Single-line header (no subtitle) + toolbar — keep in sync with layout offsets below */
const FLOW_HEADER_OFFSET = 'calc(4rem + 2px)';
const FLOW_TOOLBAR_HEIGHT = '3.25rem';
const FLOW_TOP_CHROME = `calc(${FLOW_HEADER_OFFSET} + ${FLOW_TOOLBAR_HEIGHT})`;
const WA_PREVIEW_HEADER = '#336B6B';
const WA_CHAT_BG = '#F5F5F5';
const WA_LINK_BLUE = '#027EB5';
const WA_USER_BUBBLE = '#DCF8C6';
const WA_CARD_BORDER = '#E5E7EB';
/** Fixed phone mockup — chat scrolls inside */
const PHONE_PREVIEW_HEIGHT_PX = 580;
const MAX_QUESTION_OPTIONS = 3;
const MIN_QUESTION_OPTIONS = 1;

function stepDisplayKey(stepOrder) {
  return stepOrder === 0 ? '1' : `Q${stepOrder + 1}`;
}

function isOpenerTemplateStep(step) {
  if (step?.stepOrder === 0 || step?.step_order === 0) return true;
  const stepType = step?.stepType || step?.step_type;
  return stepType === 'whatsapp_template';
}

function templateApprovalStatusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING' || s === 'IN_REVIEW') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'APPROVED' || s === 'ACTIVE')
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'REJECTED' || s === 'DISABLED') return 'bg-rose-50 text-rose-800 border-rose-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function resolveTemplateApprovalStatus(flow) {
  return (
    flow?.template_approval_status ??
    flow?.template_status ??
    flow?.approval_status ??
    ''
  );
}

function TemplateApprovalBadge({ status }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${templateApprovalStatusBadgeClass(
        status
      )}`}>
      {String(status).replace(/_/g, ' ')}
    </span>
  );
}

function placeholderSourceBadgeClass(source) {
  const s = String(source || '').toLowerCase();
  if (s === 'database') return 'bg-sky-50 text-sky-700 border-sky-200';
  if (s === 'session') return 'bg-violet-50 text-violet-700 border-violet-200';
  if (s === 'send_api') return 'bg-stone-100 text-stone-700 border-stone-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function appendPlaceholderToken(text, key) {
  const token = `{{${key}}}`;
  const base = String(text || '');
  if (!base) return token;
  const needsSpace = !/\s$/.test(base);
  return `${base}${needsSpace ? ' ' : ''}${token}`;
}

function FlowPlaceholdersContent({
  placeholders,
  isLoading,
  isError,
  onInsert,
  compact = false,
  helperText
}) {
  return (
    <div className={compact ? '' : ''}>
      {helperText ? (
        <p className="mb-3 text-sm text-gray-500">{helperText}</p>
      ) : (
        <p className="mb-3 text-sm text-gray-500">
          Use these variables in step text as{' '}
          <code className="rounded bg-stone-100 px-1.5 py-0.5 text-xs text-gray-800">
            {'{{key}}'}
          </code>
          {onInsert ? (
            <>
              . Click a row to insert into the message field.
            </>
          ) : (
            '.'
          )}
        </p>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-gray-500">Loading placeholders…</p>
      ) : isError ? (
        <p className="py-6 text-center text-sm text-rose-600">
          Could not load placeholders. Please try again.
        </p>
      ) : placeholders.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">No placeholders available.</p>
      ) : (
        <div
          className={`custom-scrollbar overflow-y-auto rounded-xl border border-stone-200 ${
            compact ? 'max-h-48' : 'max-h-[min(28rem,60vh)]'
          }`}>
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-stone-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {placeholders.map((item) => (
                <tr
                  key={item.key}
                  className={`bg-white ${onInsert ? 'cursor-pointer hover:bg-stone-50' : ''}`}
                  onClick={onInsert ? () => onInsert(item.key) : undefined}
                  title={onInsert ? `Insert {{${item.key}}}` : undefined}>
                  <td className="px-4 py-3 align-top font-mono text-xs text-gray-900">
                    {`{{${item.key}}}`}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${placeholderSourceBadgeClass(
                        item.source
                      )}`}>
                      {String(item.source || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-gray-600">{item.description}</td>
                  <td className="px-4 py-3 align-top font-medium text-gray-900">{item.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlaceholdersModal({ placeholders, isLoading, isError, onClose }) {
  return (
    <Modal name="Flow placeholders" width={52} onClose={onClose}>
      <FlowPlaceholdersContent
        placeholders={placeholders}
        isLoading={isLoading}
        isError={isError}
      />
    </Modal>
  );
}

const LEAD_AVATAR_THEMES = [
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-violet-100 text-violet-800 border-violet-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-rose-100 text-rose-800 border-rose-200'
];

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'LD';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function getLeadDisplayName(lead) {
  const name = String(lead?.user?.name ?? '').trim();
  const reference = String(lead?.reference_name ?? '').trim();
  if (name && reference) return `${name} (${reference})`;
  if (name) return name;
  if (reference) return reference;
  const id = Number(lead?.id);
  return Number.isFinite(id) ? `Lead #${id}` : 'Lead';
}

function getLeadUnitBlockLabel(lead) {
  const unitRecord =
    lead?.unit ||
    lead?.under_consideration?.[0]?.unit ||
    lead?.underConsideration?.[0]?.unit ||
    lead?.closed_deal?.unit ||
    lead?.deals?.[0]?.unit ||
    null;
  const unitName = String(unitRecord?.unitName ?? unitRecord?.name ?? '').trim();
  const blockName = String(unitRecord?.block?.name ?? '').trim();
  const parts = [blockName, unitName].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  const phone = String(lead?.user?.phone_number ?? '').trim();
  if (phone) return phone;
  const status = String(lead?.pstatus ?? '').trim();
  return status || '—';
}

function leadMatchesSearch(lead, query) {
  const q = String(query ?? '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const haystack = [
    getLeadDisplayName(lead),
    getLeadUnitBlockLabel(lead),
    lead?.user?.phone_number,
    lead?.user?.email,
    lead?.pstatus,
    lead?.sales?.name,
    lead?.projects?.[0]?.name
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function SendFlowToLeadsModal({
  flowName,
  leads,
  filteredLeads,
  isLoading,
  isError,
  isSending,
  sendProgress = 0,
  selectedLeadIds,
  searchQuery,
  onClose,
  onSearchChange,
  onToggleLead,
  onSelectAll,
  onClearSelection,
  onContinue
}) {
  const selectedCount = selectedLeadIds.length;
  const atRecipientLimit = selectedCount >= MAX_FLOW_SEND_RECIPIENTS;
  const needsChunking = selectedCount > WA_SEND_CHUNK_SIZE;

  return (
    <Modal name="Send flow" width={52} onClose={onClose} hideClose={isSending}>
      <p className="-mt-2 mb-4 text-sm text-gray-500">
        Select leads to send{' '}
        <span className="font-medium text-gray-800">{flowName || 'this flow'}</span> to. Up to{' '}
        {MAX_FLOW_SEND_RECIPIENTS} leads; sends in batches of {WA_SEND_CHUNK_SIZE}.
      </p>

      {!isLoading && !isError && leads.length > 0 ? (
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
            <label className="block text-sm font-medium text-gray-800">Recipients</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isSending || !filteredLeads.length}
                onClick={onSelectAll}
                className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline">
                Select all
              </button>
              <button
                type="button"
                disabled={isSending || selectedCount === 0}
                onClick={onClearSelection}
                className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50 disabled:no-underline">
                Clear
              </button>
            </div>
          </div>

          <div className="relative mb-2">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name, unit, or block…"
              aria-label="Search leads"
              disabled={isSending}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none ring-primary/25 placeholder:text-gray-400 focus:border-primary focus:ring-2 disabled:opacity-60"
            />
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-gray-500">Loading leads…</p>
      ) : isError ? (
        <p className="py-8 text-center text-sm text-rose-600">
          Could not load leads. Please try again.
        </p>
      ) : leads.length === 0 ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          No leads found for this project.
        </p>
      ) : filteredLeads.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          No leads match your search.
        </p>
      ) : (
        <ul className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 divide-y divide-gray-200">
          {filteredLeads.map((lead) => {
            const id = Number(lead?.id);
            const checked = Number.isFinite(id) && selectedLeadIds.includes(id);
            const checkboxDisabled =
              isSending || !Number.isFinite(id) || (atRecipientLimit && !checked);
            const displayName = getLeadDisplayName(lead);
            const subtitle = getLeadUnitBlockLabel(lead);
            const avatarSeed = String(lead?.user?.name || lead?.id || '')
              .split('')
              .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
            const avatarTheme = LEAD_AVATAR_THEMES[avatarSeed % LEAD_AVATAR_THEMES.length];

            return (
              <li key={id} className="flex items-center gap-3 bg-white px-3 py-2.5">
                <input
                  type="checkbox"
                  id={`wa-send-lead-${id}`}
                  checked={checked}
                  disabled={checkboxDisabled}
                  onChange={() => onToggleLead(id)}
                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label
                  htmlFor={`wa-send-lead-${id}`}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${avatarTheme}`}>
                    {initialsFromName(lead?.user?.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {displayName}
                    </span>
                    <span className="block truncate text-xs text-gray-500">{subtitle}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {isSending ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
          <p className="text-sm font-semibold text-amber-900">
            Sent {sendProgress}/{selectedCount}…
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Keep this page open until sending finishes
          </p>
          {needsChunking ? (
            <p className="mt-1 text-[11px] text-amber-700">
              Sending in batches of {WA_SEND_CHUNK_SIZE}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
        <p className="text-sm text-gray-600">
          {selectedCount === 0
            ? `No leads selected (max ${MAX_FLOW_SEND_RECIPIENTS})`
            : `${selectedCount} of ${MAX_FLOW_SEND_RECIPIENTS} lead${selectedCount === 1 ? '' : 's'} selected`}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60">
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={isSending || selectedCount === 0 || selectedCount > MAX_FLOW_SEND_RECIPIENTS}
            className="rounded-lg border border-primary bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
            {isSending ? `Sent ${sendProgress}/${selectedCount}` : 'Continue'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const DEMO_CHOICES = {
  0: 'get pdf',
  3: 'book_visit',
  6: 'slot_1'
};

function normalizeTrigger(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function applyTemplateVariableMap(text, variableMap) {
  if (!text) return '';
  if (!variableMap || typeof variableMap !== 'object') return text;

  return text.replace(/\{\{(\d+)\}\}/g, (_, index) => {
    const mappedKey = variableMap[`field_${index}`];
    return mappedKey ? `{{${mappedKey}}}` : `{{${index}}}`;
  });
}

const DEFAULT_META_FIELD_MAP = {
  field_1: 'lead_name',
  field_2: 'project_name',
  field_3: 'builder_name'
};

const DEFAULT_META_EXAMPLES = {
  lead_name: 'Rahul',
  project_name: 'Sunrise Heights',
  builder_name: 'ABC Builders',
  selected_slot: 'Sat 10:00 AM',
  last_answer: 'Get PDF'
};

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractMetaVariableIndices(text) {
  return [
    ...new Set(
      [...String(text || '').matchAll(/\{\{(\d+)\}\}/g)].map((match) => Number(match[1]))
    )
  ]
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
}

function getInvalidTemplatePlaceholders(text) {
  return [...String(text || '').matchAll(/\{\{([^}]+)\}\}/g)]
    .map((match) => match[1].trim())
    .filter((token) => token && !/^\d+$/.test(token));
}

function reverseTemplateVariableMap(text, variableMap) {
  if (!text) return '';
  let result = String(text);
  if (!variableMap || typeof variableMap !== 'object') return result;

  Object.entries(variableMap).forEach(([fieldKey, key]) => {
    const fieldMatch = fieldKey.match(/^field_(\d+)$/);
    if (!fieldMatch || !key) return;
    const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, 'gi');
    result = result.replace(pattern, `{{${fieldMatch[1]}}}`);
  });
  return result;
}

function normalizeTemplateBodyInput(text, variableMap = DEFAULT_META_FIELD_MAP) {
  let result = reverseTemplateVariableMap(text, variableMap);
  if (getInvalidTemplatePlaceholders(result).length) {
    result = reverseTemplateVariableMap(text, { ...DEFAULT_META_FIELD_MAP, ...variableMap });
  }
  return result.trim();
}

function buildVariableMapFromBody(metaBodyText, existingMap = {}) {
  const indices = extractMetaVariableIndices(metaBodyText);
  const map = {};
  indices.forEach((index) => {
    const fieldKey = `field_${index}`;
    map[fieldKey] =
      existingMap[fieldKey] ||
      DEFAULT_META_FIELD_MAP[fieldKey] ||
      `placeholder_${index}`;
  });
  return map;
}

function buildMetaBodyExample(metaBodyText, variableMap, placeholderLookup = {}) {
  const indices = extractMetaVariableIndices(metaBodyText);
  if (!indices.length) return null;

  const row = indices.map((index) => {
    const key = variableMap[`field_${index}`];
    return String(
      placeholderLookup[key] || DEFAULT_META_EXAMPLES[key] || `Sample ${index}`
    );
  });

  return { body_text: [row] };
}

function finalizeWhatsAppTemplateConfig(config, placeholderItems = []) {
  const lookup = Object.fromEntries((placeholderItems || []).map((item) => [item.key, item.example]));
  const next = { ...config };
  const components = [...(next.components || [])];
  const bodyIdx = components.findIndex((component) => component.type === 'BODY');
  if (bodyIdx < 0) return next;

  const metaText = normalizeTemplateBodyInput(
    components[bodyIdx].text,
    next.variable_map || {}
  );
  const invalid = getInvalidTemplatePlaceholders(metaText);
  if (invalid.length) {
    throw new Error(
      `Opener template must use {{1}}, {{2}}, etc. Invalid: ${invalid.map((key) => `{{${key}}}`).join(', ')}`
    );
  }

  const variableMap = buildVariableMapFromBody(metaText, next.variable_map || {});
  const example = buildMetaBodyExample(metaText, variableMap, lookup);

  components[bodyIdx] = {
    ...components[bodyIdx],
    type: 'BODY',
    text: metaText,
    ...(example ? { example } : {})
  };

  next.components = components;
  next.variable_map = variableMap;
  return next;
}

function validateTemplateTextError(text, variableMap = DEFAULT_META_FIELD_MAP) {
  const metaText = normalizeTemplateBodyInput(text, variableMap);
  const invalid = getInvalidTemplatePlaceholders(metaText);
  if (!invalid.length) return null;
  return `Use Meta numbered placeholders {{1}}, {{2}} in the opener. Invalid: ${invalid.map((key) => `{{${key}}}`).join(', ')}`;
}

function validateButtonLabelsError(labels, { fieldName = 'Button label' } = {}) {
  const longLabel = (labels || [])
    .map((label) => String(label || '').trim())
    .filter(Boolean)
    .find((label) => label.length > MAX_BUTTON_LABEL_LENGTH);
  if (!longLabel) return null;
  return `${fieldName} must be ${MAX_BUTTON_LABEL_LENGTH} characters or fewer. "${longLabel}" is ${longLabel.length}.`;
}

function validateFlowButtonLabels(localFlow) {
  const steps = [...(localFlow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  for (const step of steps) {
    if (step.step_type === 'whatsapp_template') {
      const btnBlock = (step.config?.components || []).find((component) => component.type === 'BUTTONS');
      const labels = (btnBlock?.buttons || []).map((button) => button.text);
      const error = validateButtonLabelsError(labels, { fieldName: 'Template button' });
      if (error) return `Step ${stepDisplayKey(step.step_order)}: ${error}`;
    }
    if (step.step_type === 'reply_buttons' || step.step_type === 'confirmation') {
      const labels = (step.config?.options || []).map((option) => option.label);
      if (labels.length > MAX_QUESTION_OPTIONS) {
        return `Step ${stepDisplayKey(step.step_order)}: At most ${MAX_QUESTION_OPTIONS} quick reply buttons allowed.`;
      }
      const error = validateButtonLabelsError(labels, { fieldName: 'Button label' });
      if (error) return `Step ${stepDisplayKey(step.step_order)}: ${error}`;
    }
  }
  return null;
}

function normalizeMessageLineBreaks(text) {
  return String(text || '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n');
}

function getStepBody(step) {
  const { step_type: stepType, config } = step;
  if (stepType === 'whatsapp_template') {
    const body = config?.components?.find((c) => c.type === 'BODY');
    return normalizeMessageLineBreaks(
      applyTemplateVariableMap(body?.text || '', config?.variable_map)
    );
  }
  return normalizeMessageLineBreaks(config?.body_template || config?.prompt_text || '');
}

function getStepOptions(step) {
  const { step_type: stepType, config } = step;
  if (stepType === 'whatsapp_template') {
    const btnBlock = config?.components?.find((c) => c.type === 'BUTTONS');
    return (btnBlock?.buttons || []).map((btn, index) => ({
      id: `opt-${index}`,
      label: btn.text,
      payload: normalizeTrigger(btn.text)
    }));
  }
  if (stepType === 'reply_buttons' || stepType === 'confirmation') {
    return (config?.options || []).map((opt, index) => ({
      id: `opt-${index}`,
      label: opt.label,
      payload: opt.payload
    }));
  }
  return [];
}

function resolveTransitionLabel(transitions, fromOrder, option, stepsByOrder) {
  const payload = normalizeTrigger(option?.payload || option?.label);
  const match = transitions.find(
    (t) =>
      t.from_step_order === fromOrder &&
      t.trigger_type === 'button' &&
      normalizeTrigger(t.trigger_value) === payload
  );
  if (!match || match.to_step_order == null) return '→ —';
  return `→ ${stepDisplayKey(match.to_step_order)}`;
}

function resolveTransitionTarget(transitions, fromOrder, option) {
  const payload = normalizeTrigger(option?.payload || option?.label);
  const match = transitions.find(
    (t) =>
      t.from_step_order === fromOrder &&
      t.trigger_type === 'button' &&
      normalizeTrigger(t.trigger_value) === payload
  );
  if (!match || match.to_step_order == null) return 'end';
  return String(match.to_step_order);
}

function upsertButtonTransition(transitions, fromOrder, triggerValue, targetValue) {
  const trigger = normalizeTrigger(triggerValue);
  const withoutCurrent = transitions.filter(
    (t) =>
      !(
        t.from_step_order === fromOrder &&
        t.trigger_type === 'button' &&
        normalizeTrigger(t.trigger_value) === trigger
      )
  );

  if (targetValue === 'end') {
    return [
      ...withoutCurrent,
      {
        from_step_order: fromOrder,
        trigger_type: 'button',
        trigger_value: trigger,
        to_step_order: null
      }
    ];
  }

  const toStepOrder = Number.parseInt(String(targetValue), 10);
  if (!Number.isFinite(toStepOrder)) return withoutCurrent;

  return [
    ...withoutCurrent,
    {
      from_step_order: fromOrder,
      trigger_type: 'button',
      trigger_value: trigger,
      to_step_order: toStepOrder
    }
  ];
}

function buildStepTransitionOptions(editorSteps, currentStepOrder) {
  return editorSteps
    .filter((step) => step.stepOrder > currentStepOrder)
    .map((step) => ({
      value: String(step.stepOrder),
      label: `→ ${step.displayKey}`
    }));
}

function removeButtonTransition(transitions, fromOrder, triggerValue) {
  const trigger = normalizeTrigger(triggerValue);
  return transitions.filter(
    (t) =>
      !(
        t.from_step_order === fromOrder &&
        t.trigger_type === 'button' &&
        normalizeTrigger(t.trigger_value) === trigger
      )
  );
}

function mapFlowToEditorSteps(flow) {
  const steps = [...(flow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  const transitions = flow?.transitions || [];
  const stepsByOrder = Object.fromEntries(steps.map((s) => [s.step_order, s]));

  return steps.map((step) => {
    const options = getStepOptions(step).map((opt) => ({
      id: `${step.id}-${opt.id}`,
      label: opt.label,
      payload: opt.payload,
      toStepOrder: resolveTransitionTarget(transitions, step.step_order, opt),
      logic: resolveTransitionLabel(transitions, step.step_order, opt, stepsByOrder)
    }));

    return {
      id: String(step.id),
      displayKey: stepDisplayKey(step.step_order),
      label: `Step ${step.step_order + 1}: ${step.title}`,
      stepType: step.step_type,
      stepOrder: step.step_order,
      prompt: getStepBody(step),
      media: step.config?.media ?? null,
      options
    };
  });
}

function slugifyFlowPayload(optionLabel, sortOrder) {
  const raw = String(optionLabel || '')
    .trim()
    .normalize('NFKC')
    .toLowerCase()
    .replace(/₹/g, 'inr')
    .replace(/[\s–—-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  if (raw) return raw.slice(0, 96);
  return `option_${sortOrder}`;
}

function titleFromStepText(text, fallback) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return fallback;
  return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
}

function createLocalStepId(stepOrder) {
  return `local-${Date.now()}-${stepOrder}`;
}

function defaultTemplateName(flowSlug) {
  const base = String(flowSlug || 'flow')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  return `${base || 'flow'}_opener_v1`;
}

function buildWhatsAppTemplateStep({
  id,
  stepOrder,
  templateName,
  bodyText,
  buttonLabels = []
}) {
  const body = String(bodyText).trim();
  const baseConfig = {
    pinggo_template_name: String(templateName).trim(),
    category: 'UTILITY',
    language: 'en_US',
    components: [{ type: 'BODY', text: body }],
    variable_map: {},
    approval_status: 'DRAFT'
  };

  const finalizedConfig = finalizeWhatsAppTemplateConfig(baseConfig);
  const finalizedComponents = [...finalizedConfig.components];
  const buttons = buttonLabels
    .map((label) => String(label).trim())
    .filter(Boolean)
    .slice(0, MAX_TEMPLATE_BUTTONS)
    .map((text) => ({ type: 'QUICK_REPLY', text }));

  if (buttons.length > 0) {
    finalizedComponents.push({ type: 'BUTTONS', buttons });
  }

  return {
    id,
    step_order: stepOrder,
    step_type: 'whatsapp_template',
    title: 'Opener',
    config: {
      ...finalizedConfig,
      components: finalizedComponents
    }
  };
}

function inferMediaType(file) {
  if (!file?.type) return 'document';
  if (String(file.type).startsWith('image/')) return 'image';
  return 'document';
}

const FLOW_ATTACHMENT_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg'
]);

const FLOW_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg']);

function isSupportedFlowAttachmentItem(item) {
  const mime = String(item?.mime_type || '').toLowerCase();
  if (FLOW_ATTACHMENT_MIME_TYPES.has(mime)) return true;
  const ext = item?.name?.split('.').pop()?.toLowerCase();
  return FLOW_ATTACHMENT_EXTENSIONS.has(ext);
}

function inferMediaTypeFromLibraryItem(item) {
  const mime = String(item?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  const ext = item?.name?.split('.').pop()?.toLowerCase();
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') return 'image';
  return 'document';
}

function buildMediaConfigFromLibraryItem(item) {
  return {
    type: inferMediaTypeFromLibraryItem(item),
    url: item.url,
    filename: item.name
  };
}

function formatMediaFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

function titleForAttachmentStep(file, bodyTemplate, stepOrder) {
  const baseName = String(file?.name || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (baseName) {
    const title = `Send ${baseName}`;
    return title.length > 48 ? `${title.slice(0, 48)}…` : title;
  }
  return titleFromStepText(bodyTemplate, `Attachment ${stepOrder + 1}`);
}

function buildMediaConfigFromUpload(file, uploadedUrl) {
  return {
    type: inferMediaType(file),
    url: uploadedUrl,
    filename: file.name
  };
}

const PLACEHOLDER_TOKEN_PATTERN = /\{\{[a-z0-9_]+\}\}/i;

function isPlaceholderMediaValue(value) {
  return PLACEHOLDER_TOKEN_PATTERN.test(String(value || ''));
}

function isValidMediaUrlValue(url) {
  const value = String(url || '').trim();
  if (!value) return false;
  if (isPlaceholderMediaValue(value)) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function filterMediaUrlPlaceholders(placeholders) {
  const items = Array.isArray(placeholders) ? placeholders : [];
  const urlPlaceholders = items.filter((item) => {
    const key = String(item?.key || '').toLowerCase();
    return key.endsWith('_url') || key.includes('pdf') || key.includes('file');
  });
  return urlPlaceholders.length > 0 ? urlPlaceholders : items;
}

function inferMediaTypeFromPlaceholderKey(key) {
  const normalized = String(key || '').toLowerCase();
  if (
    normalized.includes('image') ||
    normalized.includes('png') ||
    normalized.includes('jpg') ||
    normalized.includes('photo')
  ) {
    return 'image';
  }
  return 'document';
}

function buildMediaConfigFromPlaceholder(urlKey, filenameTemplate) {
  const key = String(urlKey || '').trim();
  const filename = String(filenameTemplate || '').trim() || `{{${key}}}.pdf`;
  return {
    type: inferMediaTypeFromPlaceholderKey(key),
    url: `{{${key}}}`,
    filename
  };
}

function defaultFilenameTemplateForPlaceholderKey(key) {
  const normalized = String(key || '').toLowerCase();
  if (normalized === 'invoice_pdf_url') {
    return 'invoice_{{invoice_number}}.pdf';
  }
  if (normalized.endsWith('_url')) {
    const base = normalized.replace(/_url$/, '').replace(/_/g, ' ');
    return `${base}.pdf`;
  }
  return `{{${key}}}.pdf`;
}

function buildStepFromAddModal({
  tab,
  messageText,
  questionText,
  questionOptions,
  file,
  uploadedMedia,
  stepOrder,
  templateName
}) {
  const id = createLocalStepId(stepOrder);

  if (stepOrder === 0) {
    const bodyText = tab === 'question' ? questionText : messageText;
    const buttonLabels = tab === 'question' ? questionOptions : [];
    return buildWhatsAppTemplateStep({
      id,
      stepOrder,
      templateName,
      bodyText,
      buttonLabels
    });
  }

  if (tab === 'question') {
    const options = questionOptions
      .map((label) => String(label).trim())
      .filter(Boolean)
      .map((label, index) => ({
        label,
        payload: slugifyFlowPayload(label, index),
        sort_order: index
      }));

    return {
      id,
      step_order: stepOrder,
      step_type: 'reply_buttons',
      title: titleFromStepText(questionText, `Question ${stepOrder + 1}`),
      config: {
        prompt_text: String(questionText).trim(),
        options
      }
    };
  }

  if (tab === 'attach_files') {
    const bodyTemplate = String(messageText).trim();
    const config = { body_template: bodyTemplate };
    if (uploadedMedia) {
      config.media = uploadedMedia;
    }

    const attachmentLabel = file?.name || uploadedMedia?.filename || '';

    return {
      id,
      step_order: stepOrder,
      step_type: 'text',
      title: titleForAttachmentStep(
        attachmentLabel ? { name: attachmentLabel } : null,
        bodyTemplate,
        stepOrder
      ),
      config
    };
  }

  if (tab === 'end_flow') {
    return {
      id,
      step_order: stepOrder,
      step_type: 'text',
      title: titleFromStepText(messageText, 'End message'),
      config: {
        body_template: String(messageText).trim()
      }
    };
  }

  return {
    id,
    step_order: stepOrder,
    step_type: 'text',
    title: titleFromStepText(messageText, `Message ${stepOrder + 1}`),
    config: {
      body_template: String(messageText).trim()
    }
  };
}

function appendTransitionsForNewStep(transitions, previousStep, newStepOrder, tab) {
  if (!previousStep) return transitions;

  const next = [...transitions];
  const fromOrder = previousStep.step_order;
  const previousOptions = getStepOptions(previousStep);

  if (previousOptions.length > 0) {
    previousOptions.forEach((opt) => {
      const triggerValue = normalizeTrigger(opt.payload || opt.label);
      const exists = next.some(
        (t) =>
          t.from_step_order === fromOrder &&
          t.trigger_type === 'button' &&
          normalizeTrigger(t.trigger_value) === triggerValue
      );
      if (!exists) {
        next.push({
          from_step_order: fromOrder,
          trigger_type: 'button',
          trigger_value: triggerValue,
          to_step_order: newStepOrder
        });
      }
    });
    return next;
  }

  if (previousStep.step_type === 'text') {
    const exists = next.some(
      (t) => t.from_step_order === fromOrder && t.trigger_type === 'any_reply'
    );
    if (!exists) {
      next.push({
        from_step_order: fromOrder,
        trigger_type: 'any_reply',
        trigger_value: '*',
        to_step_order: tab === 'end_flow' ? null : newStepOrder
      });
    }
  }

  return next;
}

function deleteStepFromFlow(localFlow, stepId) {
  const steps = [...(localFlow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  const target = steps.find((step) => String(step.id) === String(stepId));
  if (!target) return localFlow;

  if (target.step_order === 0) {
    throw new Error('The opener template (step 1) cannot be deleted.');
  }

  const deletedOrder = target.step_order;
  const nextSteps = steps
    .filter((step) => String(step.id) !== String(stepId))
    .map((step) =>
      step.step_order > deletedOrder ? { ...step, step_order: step.step_order - 1 } : step
    );

  const nextTransitions = (localFlow?.transitions || [])
    .filter((transition) => transition.from_step_order !== deletedOrder)
    .map((transition) => {
      let fromOrder = transition.from_step_order;
      let toOrder = transition.to_step_order;

      if (fromOrder > deletedOrder) fromOrder -= 1;

      if (toOrder === deletedOrder) {
        toOrder = null;
      } else if (toOrder != null && toOrder > deletedOrder) {
        toOrder -= 1;
      }

      return {
        ...transition,
        from_step_order: fromOrder,
        to_step_order: toOrder
      };
    });

  return { steps: nextSteps, transitions: nextTransitions };
}

function patchApiStepFromEditor(apiStep, editorStep) {
  const step = {
    ...apiStep,
    title: titleFromStepText(editorStep.prompt, apiStep.title),
    config: { ...(apiStep.config || {}) }
  };
  const { step_type: stepType } = step;

  if (stepType === 'whatsapp_template') {
    const withoutButtons = (step.config.components || []).filter((c) => c.type !== 'BUTTONS');
    const bodyIdx = withoutButtons.findIndex((c) => c.type === 'BODY');
    const components = [...withoutButtons];
    const metaBody = normalizeTemplateBodyInput(
      editorStep.prompt,
      step.config.variable_map || {}
    );

    if (bodyIdx >= 0) {
      components[bodyIdx] = { ...components[bodyIdx], text: metaBody };
    } else {
      components.unshift({ type: 'BODY', text: metaBody });
    }

    if (editorStep.options?.length) {
      components.push({
        type: 'BUTTONS',
        buttons: editorStep.options.map((opt) => ({
          type: 'QUICK_REPLY',
          text: opt.label
        }))
      });
    }

    step.config = finalizeWhatsAppTemplateConfig({
      ...step.config,
      components
    });
    return step;
  }

  if (stepType === 'reply_buttons') {
    step.config.prompt_text = editorStep.prompt;
    step.config.options = patchStepOptionsFromEditor(step, editorStep.options);
    return step;
  }

  if (stepType === 'confirmation') {
    step.config.body_template = editorStep.prompt;
    step.config.options = patchStepOptionsFromEditor(step, editorStep.options);
    return step;
  }

  step.config.body_template = editorStep.prompt;
  return step;
}

function patchStepOptionsFromEditor(apiStep, editorOptions) {
  const existingOptions = apiStep.config?.options || [];
  const sameLength = existingOptions.length === (editorOptions || []).length;

  return (editorOptions || []).map((opt, index) => ({
    label: opt.label,
    payload:
      sameLength && existingOptions[index]?.payload
        ? existingOptions[index].payload
        : slugifyFlowPayload(opt.label, index),
    sort_order: index
  }));
}

function finalizeOptionPayloads(options) {
  return (options || []).map((opt, index) => ({
    ...opt,
    label: String(opt.label ?? ''),
    payload: slugifyFlowPayload(opt.label, index),
    sort_order: index
  }));
}

function prepareLocalFlowForSave(localFlow) {
  const steps = [...(localFlow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  let transitions = [...(localFlow?.transitions || [])];

  const nextSteps = steps.map((step) => {
    if (step.step_type !== 'reply_buttons' && step.step_type !== 'confirmation') {
      return step;
    }

    const oldOptions = step.config?.options || [];
    const newOptions = finalizeOptionPayloads(oldOptions);

    newOptions.forEach((opt, index) => {
      const oldPayload = oldOptions[index]?.payload;
      if (!oldPayload || !opt.payload) return;
      if (normalizeTrigger(oldPayload) === normalizeTrigger(opt.payload)) return;

      transitions = transitions.map((transition) => {
        if (
          transition.from_step_order !== step.step_order ||
          transition.trigger_type !== 'button' ||
          normalizeTrigger(transition.trigger_value) !== normalizeTrigger(oldPayload)
        ) {
          return transition;
        }
        return { ...transition, trigger_value: opt.payload };
      });
    });

    return {
      ...step,
      config: {
        ...step.config,
        options: newOptions
      }
    };
  });

  return { steps: nextSteps, transitions };
}

function syncTransitionTriggers(transitions, fromOrder, editorOptions) {
  return transitions.map((transition) => {
    if (transition.from_step_order !== fromOrder || transition.trigger_type !== 'button') {
      return transition;
    }

    const match = editorOptions.find(
      (opt) => normalizeTrigger(opt.payload || opt.label) === normalizeTrigger(transition.trigger_value)
    );
    if (!match) return transition;

    const nextPayload = slugifyFlowPayload(
      match.label,
      editorOptions.findIndex((opt) => opt.id === match.id)
    );
    if (normalizeTrigger(transition.trigger_value) === normalizeTrigger(nextPayload)) {
      return transition;
    }
    return { ...transition, trigger_value: nextPayload };
  });
}

function sanitizeMediaForApi(media) {
  if (!media) return undefined;
  const { local_file: _localFile, mime_type: _mimeType, ...rest } = media;
  if (!rest.url) return undefined;
  return rest;
}

function sanitizeStepConfigForApi(stepType, config, placeholderItems = []) {
  const next = { ...(config || {}) };
  if (next.media) {
    const media = sanitizeMediaForApi(next.media);
    if (media) next.media = media;
    else delete next.media;
  }
  if (stepType === 'whatsapp_template') {
    if (next.approval_status) delete next.approval_status;
    return finalizeWhatsAppTemplateConfig(next, placeholderItems);
  }
  return next;
}

function sanitizeStepForApi(step, placeholderItems = []) {
  return {
    step_order: step.step_order,
    step_type: step.step_type,
    title: step.title,
    config: sanitizeStepConfigForApi(step.step_type, step.config, placeholderItems)
  };
}

function sanitizeTransitionForApi(transition) {
  return {
    from_step_order: transition.from_step_order,
    trigger_type: transition.trigger_type,
    trigger_value: transition.trigger_value,
    to_step_order: transition.to_step_order ?? null
  };
}

function buildFlowSavePayload(flow, localFlow, placeholderItems = []) {
  return {
    project_id: flow?.project_id,
    name: flow?.name,
    slug: flow?.slug,
    description: flow?.description?.trim() || '',
    steps: [...(localFlow?.steps || [])]
      .sort((a, b) => a.step_order - b.step_order)
      .map((step) => sanitizeStepForApi(step, placeholderItems)),
    transitions: (localFlow?.transitions || []).map(sanitizeTransitionForApi)
  };
}

function validateAddStepForm(
  tab,
  {
    messageText,
    questionText,
    questionOptions,
    file,
    libraryMedia,
    mediaUrlPlaceholderKey,
    mediaFilenameTemplate,
    templateName
  },
  { isFirstStep = false } = {}
) {
  if (isFirstStep) {
    if (!String(templateName).trim()) {
      return 'Enter a template name.';
    }
    const bodyText = tab === 'question' ? questionText : messageText;
    const templateError = validateTemplateTextError(bodyText);
    if (templateError) return templateError;
    if (!String(bodyText).trim()) {
      return 'Enter template message text.';
    }
    if (tab === 'question') {
      const filledButtons = questionOptions.map((opt) => String(opt).trim()).filter(Boolean);
      const buttonError = validateButtonLabelsError(filledButtons, { fieldName: 'Template button' });
      if (buttonError) return buttonError;
    }
    return null;
  }

  if (tab === 'question') {
    if (!String(questionText).trim()) {
      return 'Enter a question.';
    }
    const filledOptions = questionOptions.map((opt) => String(opt).trim()).filter(Boolean);
    if (!filledOptions.length) {
      return 'Add at least one option.';
    }
    const buttonError = validateButtonLabelsError(filledOptions);
    if (buttonError) return buttonError;
    return null;
  }

  if (tab === 'attach_files') {
    if (!String(messageText).trim()) {
      return 'Enter a message to send with the file.';
    }
    if (!file && !libraryMedia && !mediaUrlPlaceholderKey) {
      return 'Select a file, media library item, or URL placeholder.';
    }
    if (mediaUrlPlaceholderKey && !String(mediaFilenameTemplate || '').trim()) {
      return 'Enter a filename template for the placeholder attachment.';
    }
    return null;
  }

  if (tab === 'text_box' || tab === 'end_flow') {
    if (!String(messageText).trim()) {
      return 'Enter a message.';
    }
    return null;
  }

  return 'Select a step type.';
}

function validateFlowMediaSteps(localFlow) {
  const steps = [...(localFlow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  for (const step of steps) {
    if (step.step_type !== 'text' || !step.config?.media) continue;
    if (!isValidMediaUrlValue(step.config.media.url)) {
      return `Step ${stepDisplayKey(step.step_order)}: Upload the attachment or choose a URL placeholder before saving.`;
    }
  }
  return null;
}

function stepToPreviewMessage(step, transitions) {
  const body = getStepBody(step);
  const media = step.config?.media;
  const options = getStepOptions(step);
  const hasAnyReply = transitions.some(
    (t) => t.from_step_order === step.step_order && t.trigger_type === 'any_reply'
  );

  let text = body;
  const attachment = media?.url ? media : null;

  if (options.length > 0) {
    return {
      type: 'bot-options',
      text,
      media: attachment,
      stepOrder: step.step_order,
      options: options.map((o) => ({ label: o.label, payload: o.payload || o.label }))
    };
  }

  return {
    type: 'bot',
    text,
    media: attachment,
    stepOrder: step.step_order,
    hasAnyReply
  };
}

function buildPreviewFromFlow(flow) {
  const steps = [...(flow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
  const transitions = flow?.transitions || [];
  const stepsByOrder = Object.fromEntries(steps.map((s) => [s.step_order, s]));
  const messages = [];

  let currentOrder = steps[0]?.step_order ?? 0;
  let hops = 0;

  while (currentOrder != null && hops < 16) {
    const step = stepsByOrder[currentOrder];
    if (!step) break;

    const options = getStepOptions(step);
    const previewMsg = stepToPreviewMessage(step, transitions);
    messages.push(previewMsg);

    if (options.length > 0) {
      const demoChoice = DEMO_CHOICES[currentOrder];
      const chosen =
        options.find((o) => normalizeTrigger(o.payload || o.label) === normalizeTrigger(demoChoice)) ||
        options[0];

      messages.push({ type: 'user', text: chosen.label });

      const transition = transitions.find(
        (t) =>
          t.from_step_order === currentOrder &&
          t.trigger_type === 'button' &&
          normalizeTrigger(t.trigger_value) === normalizeTrigger(chosen.payload || chosen.label)
      );
      currentOrder = transition?.to_step_order ?? null;
    } else {
      const transition = transitions.find(
        (t) => t.from_step_order === currentOrder && t.trigger_type === 'any_reply'
      );
      if (transition) {
        messages.push({ type: 'user', text: '…' });
        currentOrder = transition.to_step_order;
      } else {
        currentOrder = null;
      }
    }

    hops += 1;
  }

  return messages;
}

function expandPlayFromStep(flow, stepsByOrder, fromStepOrder, messages) {
  const transitions = flow?.transitions || [];
  let currentOrder = fromStepOrder;
  let result = [...messages];
  let hops = 0;

  while (currentOrder != null && hops < 16) {
    const step = stepsByOrder[currentOrder];
    if (!step) break;

    result.push(stepToPreviewMessage(step, transitions));

    if (getStepOptions(step).length > 0) {
      return { messages: result, complete: false };
    }

    const anyReply = findAnyReplyTransition(transitions, currentOrder);
    if (anyReply) {
      result.push({ type: 'user', text: '…' });
      currentOrder = anyReply.to_step_order;
    } else {
      return { messages: result, complete: true };
    }

    hops += 1;
  }

  return { messages: result, complete: true };
}

function findButtonTransition(transitions, fromOrder, payload) {
  return transitions.find(
    (t) =>
      t.from_step_order === fromOrder &&
      t.trigger_type === 'button' &&
      normalizeTrigger(t.trigger_value) === normalizeTrigger(payload)
  );
}

function findAnyReplyTransition(transitions, fromOrder) {
  return transitions.find(
    (t) => t.from_step_order === fromOrder && t.trigger_type === 'any_reply'
  );
}

function MediaAttachmentCard({ media, compact = false }) {
  if (!media) return null;

  const type = media.type || 'document';
  const isImage = type === 'image';
  const Icon = isImage ? Image : FileText;
  const filename = media.filename || (isImage ? 'Image' : 'Document');
  const typeLabel = isImage ? 'Image' : 'PDF document';
  const isDynamicUrl = isPlaceholderMediaValue(media.url);

  if (compact) {
    return (
      <div
        className="mt-2 flex items-center gap-2.5 rounded-lg border px-2.5 py-2"
        style={{ borderColor: WA_CARD_BORDER, backgroundColor: '#F7F8FA' }}>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
            isImage ? 'bg-sky-100 text-sky-700' : 'bg-red-50 text-red-600'
          }`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="break-words text-xs font-medium leading-snug text-gray-800">{filename}</p>
          <p className="text-[10px] text-gray-500">{typeLabel}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white px-3 py-3 shadow-sm">
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
          isImage ? 'bg-sky-100 text-sky-700' : 'bg-red-50 text-red-600'
        }`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Attachment</p>
        <p className="break-words text-sm font-medium leading-snug text-gray-900">{filename}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {isDynamicUrl ? 'Dynamic URL placeholder' : typeLabel}
          {!isDynamicUrl && !media.url ? ' · not uploaded' : ''}
        </p>
      </div>
    </div>
  );
}

function MediaHint({ media }) {
  return media ? (
    <div className="min-w-0">
      <MediaAttachmentCard media={media} />
    </div>
  ) : null;
}

function optionLetter(index) {
  return String.fromCharCode(65 + index);
}

function FlowPreviewPhone({
  previewMessages,
  interactive = false,
  isPlayComplete = false,
  onOptionSelect
}) {
  const chatRef = useRef(null);
  const timeLabel = useMemo(
    () =>
      new Date().toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
    []
  );

  const activeMessageIndex = useMemo(() => {
    if (!interactive || isPlayComplete) return -1;
    for (let i = previewMessages.length - 1; i >= 0; i -= 1) {
      if (previewMessages[i].type === 'bot-options') return i;
    }
    return -1;
  }, [interactive, isPlayComplete, previewMessages]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [previewMessages, isPlayComplete]);

  return (
    <div
      className="flex h-full min-h-0 flex-col border-l border-gray-200 bg-[#ECECEC] lg:sticky lg:self-start lg:max-h-[calc(100vh-var(--flow-top-chrome))]"
      style={{ '--flow-top-chrome': FLOW_TOP_CHROME, top: FLOW_TOP_CHROME }}>
      <div className="flex flex-col items-center p-4">
        <div
          className="mx-auto flex w-full max-w-[300px] shrink-0 flex-col overflow-hidden rounded-[24px] border-[10px] border-gray-900 bg-gray-900 shadow-2xl"
          style={{
            height: PHONE_PREVIEW_HEIGHT_PX,
            maxHeight: 'calc(100vh - 8.5rem)'
          }}>
          <header
            className="flex shrink-0 items-center gap-2 px-3 py-2.5"
            style={{ backgroundColor: WA_PREVIEW_HEADER }}>
            <ChevronLeft className="h-5 w-5 text-white/90" />
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-[#336B6B]">
              H
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">HousinGram</p>
              <p className="text-[11px] text-white/80">online</p>
            </div>
          </header>

          <div
            ref={chatRef}
            className="custom-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-3 py-4"
            style={{ backgroundColor: WA_CHAT_BG }}>
            <div className="flex min-w-0 flex-col gap-3">
              {previewMessages.length === 0 ? (
                <p className="py-8 text-center text-xs text-gray-400">No messages to preview.</p>
              ) : null}
              {previewMessages.map((item, index) => {
                if (item.type === 'user') {
                  return (
                    <div key={index} className="flex min-w-0 justify-end">
                      <div
                        className="max-w-[85%] break-words rounded-lg rounded-tr-none px-3 py-2 text-sm text-gray-900 shadow-sm"
                        style={{ backgroundColor: WA_USER_BUBBLE }}>
                        {item.text}
                      </div>
                    </div>
                  );
                }

                if (item.type === 'bot-options') {
                  const isActive = interactive && index === activeMessageIndex;
                  return (
                    <div key={index} className="min-w-0 max-w-[92%]">
                      <div
                        className="rounded-lg border bg-white px-3 py-3 shadow-sm"
                        style={{ borderColor: WA_CARD_BORDER }}>
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
                          {item.text}
                        </p>
                        {item.media ? <MediaAttachmentCard media={item.media} compact /> : null}
                        <p className="mt-1 text-right text-[10px] text-gray-400">{timeLabel}</p>
                        <div className="mt-2 divide-y border-t" style={{ borderColor: WA_CARD_BORDER }}>
                          {item.options.map((opt) => {
                            const label = typeof opt === 'string' ? opt : opt.label;
                            const payload = typeof opt === 'string' ? opt : opt.payload || opt.label;
                            return isActive ? (
                              <button
                                key={label}
                                type="button"
                                onClick={() => onOptionSelect?.(item.stepOrder, { label, payload })}
                                className="w-full py-2.5 text-center text-sm font-medium transition-colors hover:bg-sky-50"
                                style={{ color: WA_LINK_BLUE }}>
                                {label}
                              </button>
                            ) : (
                              <div
                                key={label}
                                className="py-2.5 text-center text-sm font-medium"
                                style={{ color: WA_LINK_BLUE }}>
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={index} className="min-w-0 max-w-[92%]">
                    <div
                      className="rounded-lg border bg-white px-3 py-3 shadow-sm"
                      style={{ borderColor: WA_CARD_BORDER }}>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
                        {item.text}
                      </p>
                      {item.media ? <MediaAttachmentCard media={item.media} compact /> : null}
                      <p className="mt-1 text-right text-[10px] text-gray-400">{timeLabel}</p>
                    </div>
                  </div>
                );
              })}
              {interactive && isPlayComplete ? (
                <p className="py-2 text-center text-[11px] font-medium text-emerald-700">
                  Flow complete
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function promptLineCount(text) {
  const value = String(text || '');
  const lines = value ? value.split('\n').length : 1;
  return Math.min(8, Math.max(3, lines));
}

const STEP_MESSAGE_VIEW_CLASS =
  'w-full whitespace-pre-wrap text-sm leading-relaxed text-gray-900';
const STEP_MESSAGE_EDIT_CLASS =
  'w-full min-w-0 max-h-48 resize-y overflow-y-auto rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-gray-400';
const STEP_OPTION_FIELD_CLASS =
  'min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary/20';
const STEP_OPTION_FIELD_VIEW_CLASS =
  'min-w-0 flex-1 rounded-lg border border-transparent bg-stone-50 px-3 py-2 text-sm text-gray-800';

const STEP_BADGE_COL_CLASS = 'w-9 shrink-0';
const STEP_ROW_GAP_CLASS = 'gap-3';

function StepBadge({ label }) {
  return (
    <span
      className={`flex h-9 ${STEP_BADGE_COL_CLASS} items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-800`}>
      {label}
    </span>
  );
}

function StepBadgeSpacer() {
  return <div className={STEP_BADGE_COL_CLASS} aria-hidden="true" />;
}

function StepIndentedContent({ children, className = '' }) {
  return (
    <div className={`flex ${STEP_ROW_GAP_CLASS} ${className}`.trim()}>
      <StepBadgeSpacer />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function StepPromptField({ step, isEditing, onPromptChange, className = '' }) {
  const promptRows = promptLineCount(step.prompt);

  if (isEditing) {
    return (
      <textarea
        value={step.prompt}
        onChange={(e) => onPromptChange(step.id, e.target.value)}
        rows={promptRows}
        placeholder="Question or message text"
        className={`${STEP_MESSAGE_EDIT_CLASS} ${className}`.trim()}
      />
    );
  }

  return (
    <p className={`${STEP_MESSAGE_VIEW_CLASS} ${className}`.trim()}>
      {step.prompt || '—'}
    </p>
  );
}

function OptionTransitionSelect({ value, logicLabel, isEditing, targets, onChange }) {
  if (!isEditing) {
    return (
      <div
        className="flex min-w-[10rem] max-w-[12rem] shrink-0 items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800"
        title={logicLabel}>
        <span className="truncate">{logicLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
      </div>
    );
  }

  return (
    <div className="relative min-w-[10rem] max-w-[12rem] shrink-0">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-lg border border-sky-200 bg-sky-50 py-2 pl-3 pr-8 text-xs font-medium text-sky-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
        aria-label="Next step after this option">
        <option value="end">→ —</option>
        {targets.map((target) => (
          <option key={target.value} value={target.value}>
            {target.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sky-700 opacity-70" />
    </div>
  );
}

function QuickRepliesSection({
  step,
  allSteps,
  isContentEditing,
  isTransitionEditing,
  onOptionChange,
  onOptionTransitionChange,
  onAddOption,
  onRemoveOption
}) {
  if (!step.options.length) return null;

  const transitionTargets = buildStepTransitionOptions(allSteps, step.stepOrder);
  const canRemoveOption = isContentEditing && step.options.length > MIN_QUESTION_OPTIONS;
  const canAddOption =
    isContentEditing &&
    step.options.length <
      (isOpenerTemplateStep(step) ? MAX_TEMPLATE_BUTTONS : MAX_QUESTION_OPTIONS);
  const optionInputClass = isContentEditing ? STEP_OPTION_FIELD_CLASS : STEP_OPTION_FIELD_VIEW_CLASS;

  return (
    <div className="px-4 pb-4 pt-3">
      <StepIndentedContent>
        <p className="mb-3 text-xs font-medium text-gray-600">
          Quick reply buttons{' '}
          <span className="text-gray-400">(max {MAX_BUTTON_LABEL_LENGTH} chars)</span>
        </p>
        <div className="space-y-2">
          {step.options.map((opt, index) => (
            <div key={opt.id} className="flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-xs font-bold text-gray-500">
                {optionLetter(index)}
              </span>
              <input
                type="text"
                value={opt.label}
                maxLength={MAX_BUTTON_LABEL_LENGTH}
                onChange={(e) => onOptionChange(step.id, opt.id, e.target.value)}
                readOnly={!isContentEditing}
                className={optionInputClass}
              />
              <OptionTransitionSelect
                value={opt.toStepOrder ?? 'end'}
                logicLabel={opt.logic}
                isEditing={isTransitionEditing}
                targets={transitionTargets}
                onChange={(targetValue) =>
                  onOptionTransitionChange(step.id, opt.id, opt.payload || opt.label, targetValue)
                }
              />
              {canRemoveOption ? (
                <button
                  type="button"
                  onClick={() => onRemoveOption(step.id, opt.id)}
                  className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-rose-500"
                  aria-label="Remove option">
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
        </div>

        {canAddOption ? (
          <button
            type="button"
            onClick={() => onAddOption(step.id)}
            className="mt-3 text-sm font-semibold text-[#027EB5] hover:underline">
            +Add option
          </button>
        ) : null}
      </StepIndentedContent>
    </div>
  );
}

function StepDeleteButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
      aria-label={label || 'Delete step'}>
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

function InternalStepSection({
  step,
  allSteps,
  isEditing,
  onPromptChange,
  onOptionChange,
  onOptionTransitionChange,
  onAddOption,
  onRemoveOption,
  onRemoveStep
}) {
  const canDelete = isEditing && step.stepOrder > 0;

  return (
    <section className="border-t border-gray-200">
      <div className="px-4 py-3">
        <div className={`flex items-start ${STEP_ROW_GAP_CLASS}`}>
          <StepBadge label={step.displayKey} />
          <div className="min-w-0 flex-1">
            <StepPromptField
              step={step}
              isEditing={isEditing}
              onPromptChange={onPromptChange}
            />
            {step.media ? (
              <div className="mt-3">
                <MediaAttachmentCard media={step.media} />
              </div>
            ) : null}
          </div>
          {canDelete ? (
            <StepDeleteButton
              onClick={() => onRemoveStep(step.id)}
              label={`Delete step ${step.displayKey}`}
            />
          ) : null}
        </div>
      </div>

      <QuickRepliesSection
        step={step}
        allSteps={allSteps}
        isContentEditing={isEditing}
        isTransitionEditing={isEditing}
        onOptionChange={onOptionChange}
        onOptionTransitionChange={onOptionTransitionChange}
        onAddOption={onAddOption}
        onRemoveOption={onRemoveOption}
      />

    </section>
  );
}

function TemplateNameField({ value, onChange }) {
  return (
    <div className="mb-5">
      <label className="mb-1.5 block text-sm font-semibold text-gray-900">Template name</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="site_visit_opener_v1"
        className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
      <p className="mt-1.5 text-xs text-gray-500">
        Meta template body uses {'{{1}}'}, {'{{2}}'} — mapped to lead_name, project_name.
      </p>
    </div>
  );
}

function AttachFilesStepPicker({
  projectId,
  messageText,
  onMessageTextChange,
  onMessageFieldFocus,
  selectedFile,
  onFileChange,
  selectedLibraryMedia,
  onLibraryMediaChange,
  mediaUrlPlaceholderKey,
  onMediaUrlPlaceholderKeyChange,
  mediaFilenameTemplate,
  onMediaFilenameTemplateChange,
  placeholders,
  isPlaceholdersLoading,
  isPlaceholdersError,
  libraryItems,
  isLibraryLoading,
  isLibraryError,
  isConfirming
}) {
  const fileInputRef = useRef(null);
  const [attachSource, setAttachSource] = useState(
    mediaUrlPlaceholderKey ? 'placeholder' : 'library'
  );

  const supportedLibraryItems = useMemo(
    () => (libraryItems || []).filter(isSupportedFlowAttachmentItem),
    [libraryItems]
  );

  const urlPlaceholders = useMemo(
    () => filterMediaUrlPlaceholders(placeholders),
    [placeholders]
  );

  const switchAttachSource = (source) => {
    setAttachSource(source);
    if (source === 'library') {
      onFileChange(null);
      onMediaUrlPlaceholderKeyChange('');
    } else if (source === 'upload') {
      onLibraryMediaChange(null);
      onMediaUrlPlaceholderKeyChange('');
    } else {
      onFileChange(null);
      onLibraryMediaChange(null);
    }
  };

  const handleSelectUrlPlaceholder = (key) => {
    onMediaUrlPlaceholderKeyChange(key);
    if (!String(mediaFilenameTemplate || '').trim()) {
      onMediaFilenameTemplateChange(defaultFilenameTemplateForPlaceholderKey(key));
    }
  };

  return (
    <div>
      <p className="mb-3 text-sm font-semibold text-gray-900">Message to send</p>
      <textarea
        value={messageText}
        onChange={(e) => onMessageTextChange(e.target.value)}
        onFocus={() => onMessageFieldFocus('messageText')}
        rows={3}
        placeholder="Here is your invoice for {{unit_name}} ({{invoice_number}})."
        disabled={isConfirming}
        className="mb-4 w-full resize-y rounded-xl border border-stone-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
      />

      <p className="mb-2 text-sm font-semibold text-gray-900">Attachment</p>
      <div className="mb-4 flex gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1">
        <button
          type="button"
          disabled={isConfirming}
          onClick={() => switchAttachSource('library')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60 sm:text-sm ${
            attachSource === 'library'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}>
          <Paperclip className="h-4 w-4 shrink-0" />
          Media library
        </button>
        <button
          type="button"
          disabled={isConfirming}
          onClick={() => switchAttachSource('upload')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60 sm:text-sm ${
            attachSource === 'upload'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}>
          <Upload className="h-4 w-4 shrink-0" />
          Upload file
        </button>
        <button
          type="button"
          disabled={isConfirming}
          onClick={() => switchAttachSource('placeholder')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition-colors disabled:opacity-60 sm:text-sm ${
            attachSource === 'placeholder'
              ? 'bg-white text-primary shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}>
          <Braces className="h-4 w-4 shrink-0" />
          Placeholder URL
        </button>
      </div>

      {attachSource === 'placeholder' ? (
        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-4">
          <p className="text-sm text-gray-500">
            Pick a URL placeholder (e.g.{' '}
            <code className="rounded bg-stone-100 px-1 text-xs">{'{{invoice_pdf_url}}'}</code>). The
            file is resolved when the flow is sent.{' '}
            <span className="font-medium text-gray-600">(Invoice and payment flow only)</span>
          </p>

          {isPlaceholdersLoading ? (
            <p className="py-4 text-center text-sm text-gray-500">Loading placeholders…</p>
          ) : isPlaceholdersError ? (
            <p className="py-4 text-center text-sm text-rose-600">Could not load placeholders.</p>
          ) : urlPlaceholders.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No URL placeholders available.</p>
          ) : (
            <ul className="custom-scrollbar max-h-44 divide-y divide-stone-100 overflow-y-auto rounded-lg border border-stone-100">
              {urlPlaceholders.map((item) => {
                const selected = mediaUrlPlaceholderKey === item.key;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      disabled={isConfirming}
                      onClick={() => handleSelectUrlPlaceholder(item.key)}
                      className={`flex w-full items-start gap-3 px-3 py-3 text-left transition-colors disabled:opacity-60 ${
                        selected ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : 'hover:bg-stone-50'
                      }`}>
                      <span
                        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-700`}>
                        <Braces className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono text-xs font-semibold text-gray-900">
                          {`{{${item.key}}}`}
                        </span>
                        {item.description ? (
                          <span className="mt-0.5 block text-xs text-gray-500">{item.description}</span>
                        ) : null}
                        {item.example ? (
                          <span className="mt-1 block truncate text-[11px] text-gray-400">
                            e.g. {item.example}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`mt-1 h-4 w-4 shrink-0 rounded-full border ${
                          selected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'
                        }`}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div>
            <label
              htmlFor="wa-attach-filename-template"
              className="mb-1.5 block text-sm font-medium text-gray-800">
              Filename template
            </label>
            <input
              id="wa-attach-filename-template"
              type="text"
              value={mediaFilenameTemplate}
              onChange={(e) => onMediaFilenameTemplateChange(e.target.value)}
              onFocus={() => onMessageFieldFocus('placeholderFilename')}
              disabled={isConfirming || !mediaUrlPlaceholderKey}
              placeholder="invoice_{{invoice_number}}.pdf"
              className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Use placeholders in the filename. Open the Placeholders tab to insert keys.
            </p>
          </div>
        </div>
      ) : attachSource === 'library' ? (
        <div className="rounded-xl border border-stone-200 bg-white">
          {!projectId ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">
              This flow has no project. Cannot load media library.
            </p>
          ) : isLibraryLoading ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">Loading media library…</p>
          ) : isLibraryError ? (
            <p className="px-4 py-8 text-center text-sm text-rose-600">
              Could not load media library. Try uploading from your computer.
            </p>
          ) : supportedLibraryItems.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">
              No PDF, PNG, or JPG files in this project&apos;s media library. Upload one using the
              Upload file tab.
            </p>
          ) : (
            <ul className="custom-scrollbar max-h-56 divide-y divide-stone-100 overflow-y-auto">
              {supportedLibraryItems.map((item) => {
                const selected = selectedLibraryMedia?.id === item.id;
                const isImage = inferMediaTypeFromLibraryItem(item) === 'image';
                const Icon = isImage ? Image : FileText;

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      disabled={isConfirming}
                      onClick={() => onLibraryMediaChange(item)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                        selected ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : 'hover:bg-stone-50'
                      }`}>
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                          isImage ? 'bg-sky-100 text-sky-700' : 'bg-red-50 text-red-600'
                        }`}>
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">
                          {item.name}
                        </span>
                        {item.file_size_bytes ? (
                          <span className="block text-xs text-gray-500">
                            {formatMediaFileSize(item.file_size_bytes)}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`h-4 w-4 shrink-0 rounded-full border ${
                          selected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'
                        }`}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,image/png,image/jpeg,application/pdf"
            className="hidden"
            disabled={isConfirming}
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isConfirming}
            className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-primary/30 bg-[#E9F5F6]/60 px-6 py-14 text-center transition-colors hover:border-primary/50 hover:bg-[#E9F5F6] disabled:cursor-not-allowed disabled:opacity-60">
            <Upload className="mb-3 h-8 w-8 text-primary" strokeWidth={1.75} />
            <span className="text-sm font-semibold text-primary">Click to upload</span>
            <span className="mt-1 text-xs text-gray-500">PDF, PNG, JPG • max upto 16 MB</span>
            {selectedFile ? (
              <span className="mt-3 text-xs font-medium text-gray-700">{selectedFile.name}</span>
            ) : null}
          </button>
        </>
      )}
    </div>
  );
}

function AddFlowStepModal({
  isOpen,
  isFirstStep,
  activeTab,
  onTabChange,
  onClose,
  templateName,
  onTemplateNameChange,
  messageText,
  onMessageTextChange,
  questionText,
  onQuestionTextChange,
  questionOptions,
  onQuestionOptionChange,
  onAddQuestionOption,
  onRemoveQuestionOption,
  projectId,
  selectedFile,
  onFileChange,
  selectedLibraryMedia,
  onLibraryMediaChange,
  mediaUrlPlaceholderKey,
  onMediaUrlPlaceholderKeyChange,
  mediaFilenameTemplate,
  onMediaFilenameTemplateChange,
  libraryItems,
  isLibraryLoading,
  isLibraryError,
  isConfirming,
  onConfirm,
  placeholders,
  isPlaceholdersLoading,
  isPlaceholdersError
}) {
  const [activeMessageField, setActiveMessageField] = useState('messageText');

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === 'placeholders') return;
    setActiveMessageField(activeTab === 'question' ? 'questionText' : 'messageText');
  }, [isOpen, activeTab]);

  if (!isOpen || typeof document === 'undefined') return null;

  const stepTabs = [...(isFirstStep ? FIRST_STEP_TABS : ADD_STEP_TABS), PLACEHOLDERS_TAB];
  const maxOptions = isFirstStep ? MAX_TEMPLATE_BUTTONS : MAX_QUESTION_OPTIONS;
  const confirmLabel = activeTab === 'text_box' ? 'Create' : 'Confirm';
  const isPlaceholdersTab = activeTab === 'placeholders';

  const handleInsertPlaceholder = (key) => {
    if (isFirstStep) return;
    if (activeMessageField === 'questionText') {
      onQuestionTextChange(appendPlaceholderToken(questionText, key));
      return;
    }
    if (activeMessageField === 'placeholderFilename') {
      onMediaFilenameTemplateChange(appendPlaceholderToken(mediaFilenameTemplate, key));
      return;
    }
    onMessageTextChange(appendPlaceholderToken(messageText, key));
  };

  const placeholdersHelperText = isFirstStep
    ? 'Step 1 uses Meta numbered placeholders {{1}}, {{2}} in the template body. The list below shows how they map to lead and project fields.'
    : activeTab === 'attach_files'
      ? 'Click a placeholder to insert into the focused message or filename field.'
      : undefined;

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/20 p-4"
      onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true">
        <div className="rounded-t-xl bg-[#E9F5F6] px-4 pt-4">
          <div className="mb-3 flex items-start justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-white/60 hover:text-gray-700"
              aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex gap-1 overflow-x-auto border-b border-stone-200/80">
            {stepTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`shrink-0 border-b-2 px-3 pb-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {tab.id === 'placeholders' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Braces className="h-4 w-4" />
                    {tab.label}
                  </span>
                ) : (
                  tab.label
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="custom-scrollbar flex-1 overflow-y-auto px-8 py-6">
          {isFirstStep && !isPlaceholdersTab ? (
            <p className="mb-4 text-sm text-gray-500">
              Step 1 is a Meta WhatsApp template (required for cold outreach). Reply buttons are
              optional on the Question tab.
            </p>
          ) : null}

          {activeTab === 'text_box' ? (
            <div>
              {isFirstStep ? <TemplateNameField value={templateName} onChange={onTemplateNameChange} /> : null}
              <p className="mb-3 text-sm font-semibold text-gray-900">
                {isFirstStep ? 'Template message' : 'Message to send'}
              </p>
              <textarea
                value={messageText}
                onChange={(e) => onMessageTextChange(e.target.value)}
                onFocus={() => setActiveMessageField('messageText')}
                rows={5}
                placeholder={
                  isFirstStep
                    ? "Hi {{1}}, we'd love to show you {{2}}."
                    : 'Add a link/ a single message'
                }
                className="w-full resize-y rounded-xl border border-stone-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          ) : null}

          {activeTab === 'attach_files' ? (
            <AttachFilesStepPicker
              projectId={projectId}
              messageText={messageText}
              onMessageTextChange={onMessageTextChange}
              onMessageFieldFocus={setActiveMessageField}
              selectedFile={selectedFile}
              onFileChange={onFileChange}
              selectedLibraryMedia={selectedLibraryMedia}
              onLibraryMediaChange={onLibraryMediaChange}
              mediaUrlPlaceholderKey={mediaUrlPlaceholderKey}
              onMediaUrlPlaceholderKeyChange={onMediaUrlPlaceholderKeyChange}
              mediaFilenameTemplate={mediaFilenameTemplate}
              onMediaFilenameTemplateChange={onMediaFilenameTemplateChange}
              placeholders={placeholders}
              isPlaceholdersLoading={isPlaceholdersLoading}
              isPlaceholdersError={isPlaceholdersError}
              libraryItems={libraryItems}
              isLibraryLoading={isLibraryLoading}
              isLibraryError={isLibraryError}
              isConfirming={isConfirming}
            />
          ) : null}

          {activeTab === 'question' ? (
            <div>
              {isFirstStep ? <TemplateNameField value={templateName} onChange={onTemplateNameChange} /> : null}
              <p className="mb-3 text-sm font-semibold text-gray-900">
                {isFirstStep ? 'Template message' : 'Add a question'}
              </p>
              <textarea
                value={questionText}
                onChange={(e) => onQuestionTextChange(e.target.value)}
                onFocus={() => setActiveMessageField('questionText')}
                rows={4}
                placeholder={
                  isFirstStep
                    ? "Hi {{1}}, we'd love to show you {{2}}. Tap below to continue."
                    : 'Type a question..'
                }
                className="mb-5 w-full resize-y rounded-xl border border-stone-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <p className="mb-2 text-xs font-medium text-gray-600">
                {isFirstStep ? 'Quick reply buttons (optional)' : 'Quick reply buttons'}{' '}
                <span className="text-gray-400">(max {MAX_BUTTON_LABEL_LENGTH} chars)</span>
              </p>
              <div className="space-y-2">
                {questionOptions.map((opt, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-4 shrink-0 text-xs font-bold text-gray-500">
                      {optionLetter(index)}
                    </span>
                    <input
                      type="text"
                      value={opt}
                      maxLength={MAX_BUTTON_LABEL_LENGTH}
                      onChange={(e) => onQuestionOptionChange(index, e.target.value)}
                      placeholder="Option label"
                      className="min-w-0 flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    {isFirstStep || questionOptions.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => onRemoveQuestionOption(index)}
                        className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-50 hover:text-rose-500"
                        aria-label="Remove option">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              {questionOptions.length < maxOptions ? (
                <button
                  type="button"
                  onClick={onAddQuestionOption}
                  className="mt-3 text-sm font-semibold text-primary hover:underline">
                  Add option
                </button>
              ) : (
                <p className="mt-3 text-xs text-gray-500">Up to {maxOptions} options</p>
              )}
            </div>
          ) : null}

          {activeTab === 'end_flow' ? (
            <div>
              <p className="mb-3 text-sm font-semibold text-gray-900">Message to send</p>
              <textarea
                value={messageText}
                onChange={(e) => onMessageTextChange(e.target.value)}
                onFocus={() => setActiveMessageField('messageText')}
                rows={5}
                placeholder="Add a single message"
                className="w-full resize-y rounded-xl border border-stone-200 px-4 py-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          ) : null}

          {isPlaceholdersTab ? (
            <FlowPlaceholdersContent
              placeholders={placeholders}
              isLoading={isPlaceholdersLoading}
              isError={isPlaceholdersError}
              onInsert={isFirstStep ? undefined : handleInsertPlaceholder}
              helperText={placeholdersHelperText}
            />
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-stone-100 px-8 py-5">
          <Button type="button" displayType="secondary" onClick={onClose} disabled={isConfirming}>
            Cancel
          </Button>
          {!isPlaceholdersTab ? (
            <Button type="button" onClick={onConfirm} disabled={isConfirming}>
              {isConfirming
                ? activeTab === 'attach_files' && selectedFile
                  ? 'Uploading…'
                  : 'Adding…'
                : confirmLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function resetAddStepFormState(flowSlug, isFirstStep = false) {
  return {
    templateName: defaultTemplateName(flowSlug),
    messageText: '',
    questionText: '',
    questionOptions: isFirstStep ? [] : [''],
    selectedFile: null,
    selectedLibraryMedia: null,
    mediaUrlPlaceholderKey: '',
    mediaFilenameTemplate: ''
  };
}

function AddQuestionRow({ onClick }) {
  return (
    <div className="px-4 py-3">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-between rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50">
        <span>Add question/ text</span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>
    </div>
  );
}

function EmptyFlowCard({ onAddQuestion }) {
  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-6 py-10 text-center text-sm text-gray-500">
        Add the opener Meta template to start this flow.
      </div>
      <AddQuestionRow onClick={onAddQuestion} />
    </article>
  );
}
function FlowStepsCard({
  steps,
  isEditing,
  onPromptChange,
  onOptionChange,
  onOptionTransitionChange,
  onAddOption,
  onRemoveOption,
  onRemoveStep,
  onOpenAddStep
}) {
  const [firstStep, ...restSteps] = steps;
  const openerLocked = isOpenerTemplateStep(firstStep);

  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="border-b border-gray-200 bg-gray-100 px-4 py-3">
        <div className={`flex items-start ${STEP_ROW_GAP_CLASS}`}>
          <StepBadge label={firstStep.displayKey} />
          <div className="min-w-0 flex-1">
            <StepPromptField
              step={firstStep}
              isEditing={isEditing && !openerLocked}
              onPromptChange={onPromptChange}
            />
          </div>
        </div>
      </header>

      <div className="bg-white">
        <QuickRepliesSection
          step={firstStep}
          allSteps={steps}
          isContentEditing={isEditing && !openerLocked}
          isTransitionEditing={isEditing}
          onOptionChange={onOptionChange}
          onOptionTransitionChange={onOptionTransitionChange}
          onAddOption={onAddOption}
          onRemoveOption={onRemoveOption}
        />

        {firstStep.media ? (
          <div className="px-4 pb-3">
            <MediaHint media={firstStep.media} />
          </div>
        ) : null}

        {restSteps.map((step) => (
          <InternalStepSection
            key={step.id}
            step={step}
            allSteps={steps}
            isEditing={isEditing}
            onPromptChange={onPromptChange}
            onOptionChange={onOptionChange}
            onOptionTransitionChange={onOptionTransitionChange}
            onAddOption={onAddOption}
            onRemoveOption={onRemoveOption}
            onRemoveStep={onRemoveStep}
          />
        ))}

        {isEditing ? <AddQuestionRow onClick={onOpenAddStep} /> : null}
      </div>
    </article>
  );
}

const WhatsAppFlowEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { flowId } = useParams();
  const { collapsed } = useSidebar();
  const queryClient = useQueryClient();

  const {
    data: flow,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['whatsappFlow', flowId],
    queryFn: () => getWhatsAppFlowById(flowId),
    enabled: Boolean(flowId)
  });

  const [localFlow, setLocalFlow] = useState({ steps: [], transitions: [] });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);
  const [isSendingFlow, setIsSendingFlow] = useState(false);
  const [sendFlowProgress, setSendFlowProgress] = useState(0);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('preview');
  const [playMessages, setPlayMessages] = useState([]);
  const [isPlayComplete, setIsPlayComplete] = useState(false);
  const [placeholdersModalOpen, setPlaceholdersModalOpen] = useState(false);
  const [addStepModalOpen, setAddStepModalOpen] = useState(false);
  const [addStepTab, setAddStepTab] = useState('text_box');
  const [addStepTemplateName, setAddStepTemplateName] = useState('');
  const [addStepMessageText, setAddStepMessageText] = useState('');
  const [addStepQuestionText, setAddStepQuestionText] = useState('');
  const [addStepQuestionOptions, setAddStepQuestionOptions] = useState(['']);
  const [addStepFile, setAddStepFile] = useState(null);
  const [addStepLibraryMedia, setAddStepLibraryMedia] = useState(null);
  const [addStepMediaUrlPlaceholderKey, setAddStepMediaUrlPlaceholderKey] = useState('');
  const [addStepMediaFilenameTemplate, setAddStepMediaFilenameTemplate] = useState('');
  const [isAddingStep, setIsAddingStep] = useState(false);

  const isFirstStepAdd = localFlow.steps.length === 0;

  const {
    data: placeholders = [],
    isLoading: isPlaceholdersLoading,
    isError: isPlaceholdersError
  } = useQuery({
    queryKey: ['whatsappFlowPlaceholders'],
    queryFn: getWhatsAppFlowPlaceholders,
    enabled: placeholdersModalOpen || isEditing || addStepModalOpen
  });

  const projectId = flow?.project_id;

  const {
    data: sendLeadsResponse,
    isLoading: isSendLeadsLoading,
    isError: isSendLeadsError
  } = useQuery({
    queryKey: ['flowSendLeads', projectId],
    queryFn: () => getLeadsByStatusAndProject(projectId, undefined, undefined, 1, 500),
    enabled: sendModalOpen && Boolean(projectId)
  });

  const {
    data: campaignMediaItems = [],
    isLoading: isCampaignMediaLoading,
    isError: isCampaignMediaError
  } = useQuery({
    queryKey: ['campaignMediaLibrary', projectId],
    queryFn: () => getCampaignMediaLibrary(projectId),
    enabled: addStepModalOpen && addStepTab === 'attach_files' && Boolean(projectId)
  });

  const sendModalLeads = useMemo(() => {
    const rows = sendLeadsResponse?.data;
    if (!Array.isArray(rows)) return [];
    return rows.filter((lead) => String(lead?.status ?? '').toLowerCase() !== 'closed');
  }, [sendLeadsResponse]);

  const filteredSendModalLeads = useMemo(
    () => sendModalLeads.filter((lead) => leadMatchesSearch(lead, leadSearchQuery)),
    [sendModalLeads, leadSearchQuery]
  );

  useEffect(() => {
    if (flow) {
      const steps = [...(flow.steps || [])]
        .sort((a, b) => a.step_order - b.step_order)
        .map((step) => {
          if (step.step_type !== 'whatsapp_template') return step;
          try {
            return {
              ...step,
              config: finalizeWhatsAppTemplateConfig(step.config || {})
            };
          } catch {
            return step;
          }
        });
      setLocalFlow({
        steps,
        transitions: [...(flow.transitions || [])]
      });
      setIsEditing(false);
      setViewMode('preview');
      setPlayMessages([]);
      setIsPlayComplete(false);
    }
  }, [flow]);

  const mergedFlow = useMemo(
    () =>
      flow
        ? {
            ...flow,
            steps: localFlow.steps,
            transitions: localFlow.transitions
          }
        : null,
    [flow, localFlow]
  );

  const editorSteps = useMemo(() => {
    if (!mergedFlow) return [];
    return mapFlowToEditorSteps(mergedFlow);
  }, [mergedFlow]);

  const flowTitle = flow?.name || location.state?.flowName || 'WhatsApp flow';
  const flowDescription =
    flow?.description?.trim() ||
    location.state?.flowDescription?.trim() ||
    'Build your WhatsApp question flow step by step';
  const templateApprovalStatus = useMemo(
    () => resolveTemplateApprovalStatus(flow),
    [flow]
  );

  const hasTemplateStep = useMemo(
    () => localFlow.steps.some((step) => step.step_type === 'whatsapp_template'),
    [localFlow.steps]
  );

  const autoPreviewMessages = useMemo(() => {
    if (!mergedFlow) return [];
    return buildPreviewFromFlow(mergedFlow);
  }, [mergedFlow]);

  const previewMessages = viewMode === 'play' ? playMessages : autoPreviewMessages;

  const flowStepsByOrder = useMemo(() => {
    const steps = [...(mergedFlow?.steps || [])].sort((a, b) => a.step_order - b.step_order);
    return Object.fromEntries(steps.map((s) => [s.step_order, s]));
  }, [mergedFlow]);

  const handlePreview = () => {
    setViewMode('preview');
    setPlayMessages([]);
    setIsPlayComplete(false);
  };

  const handlePlay = () => {
    if (!mergedFlow) return;
    setViewMode('play');
    const steps = [...(mergedFlow.steps || [])].sort((a, b) => a.step_order - b.step_order);
    const firstOrder = steps[0]?.step_order;
    if (firstOrder == null) {
      setPlayMessages([]);
      setIsPlayComplete(true);
      return;
    }
    const { messages, complete } = expandPlayFromStep(mergedFlow, flowStepsByOrder, firstOrder, []);
    setPlayMessages(messages);
    setIsPlayComplete(complete);
  };

  const handlePlayOptionSelect = (stepOrder, option) => {
    if (viewMode !== 'play' || isPlayComplete || !mergedFlow) return;

    const transition = findButtonTransition(
      mergedFlow.transitions || [],
      stepOrder,
      option.payload || option.label
    );
    const nextOrder = transition?.to_step_order ?? null;
    const withUser = [...playMessages, { type: 'user', text: option.label }];

    if (nextOrder == null) {
      setPlayMessages(withUser);
      setIsPlayComplete(true);
      return;
    }

    const { messages, complete } = expandPlayFromStep(
      mergedFlow,
      flowStepsByOrder,
      nextOrder,
      withUser
    );
    setPlayMessages(messages);
    setIsPlayComplete(complete);
  };

  const updatePrompt = (stepId, value) => {
    if (!isEditing) return;
    try {
      setLocalFlow((prev) => ({
        ...prev,
        steps: prev.steps.map((step) => {
          if (String(step.id) !== String(stepId)) return step;
          if (step.step_order === 0) return step;
          const editorStep = {
            ...mapFlowToEditorSteps({
              steps: [step],
              transitions: prev.transitions
            })[0],
            prompt: value
          };
          return patchApiStepFromEditor(step, editorStep);
        })
      }));
    } catch (err) {
      toast.error(err?.message || 'Invalid template text.');
    }
  };

  const updateOptionTransition = (stepId, optionId, triggerValue, targetValue) => {
    if (!isEditing) return;

    setLocalFlow((prev) => {
      const fromStep = prev.steps.find((step) => String(step.id) === String(stepId));
      if (!fromStep) return prev;

      const editorStep = mapFlowToEditorSteps({
        steps: [fromStep],
        transitions: prev.transitions
      })[0];
      const option = editorStep?.options?.find((opt) => opt.id === optionId);
      const trigger = option?.payload || triggerValue || option?.label;
      if (!trigger) return prev;

      return {
        ...prev,
        transitions: upsertButtonTransition(
          prev.transitions,
          fromStep.step_order,
          trigger,
          targetValue
        )
      };
    });
  };

  const updateOption = (stepId, optionId, value) => {
    if (!isEditing) return;
    setLocalFlow((prev) => {
      const target = prev.steps.find((step) => String(step.id) === String(stepId));
      if (!target || target.step_order === 0) return prev;

      const optionIndex = Number(String(optionId).match(/-opt-(\d+)$/)?.[1]);
      if (!Number.isFinite(optionIndex) || optionIndex < 0) return prev;

      const steps = prev.steps.map((step) => {
        if (String(step.id) !== String(stepId)) return step;
        if (step.step_type !== 'reply_buttons' && step.step_type !== 'confirmation') {
          return step;
        }

        const options = [...(step.config?.options || [])];
        if (optionIndex >= options.length) return step;

        options[optionIndex] = { ...options[optionIndex], label: value };
        return {
          ...step,
          config: {
            ...step.config,
            options
          }
        };
      });

      return { ...prev, steps };
    });
  };

  const addOption = (stepId) => {
    if (!isEditing) return;
    setLocalFlow((prev) => {
      const step = prev.steps.find((item) => String(item.id) === String(stepId));
      if (!step || step.step_order === 0) return prev;

      const editorStep = mapFlowToEditorSteps({
        steps: [step],
        transitions: prev.transitions
      })[0];

      if (!editorStep?.options || editorStep.options.length >= MAX_QUESTION_OPTIONS) return prev;

      const nextEditorStep = {
        ...editorStep,
        options: [
          ...editorStep.options,
          {
            id: `${step.id}-opt-new-${Date.now()}`,
            label: 'New option',
            payload: slugifyFlowPayload('New option', editorStep.options.length),
            logic: '→ —',
            toStepOrder: 'end'
          }
        ]
      };

      return {
        ...prev,
        steps: prev.steps.map((item) =>
          String(item.id) === String(stepId) ? patchApiStepFromEditor(step, nextEditorStep) : item
        )
      };
    });
  };

  const removeOption = (stepId, optionId) => {
    if (!isEditing) return;
    setLocalFlow((prev) => {
      const step = prev.steps.find((item) => String(item.id) === String(stepId));
      if (!step || step.step_order === 0) return prev;

      const editorStep = mapFlowToEditorSteps({
        steps: [step],
        transitions: prev.transitions
      })[0];
      const option = editorStep?.options?.find((opt) => opt.id === optionId);
      if (!option || editorStep.options.length <= MIN_QUESTION_OPTIONS) return prev;

      const nextEditorStep = {
        ...editorStep,
        options: editorStep.options.filter((opt) => opt.id !== optionId)
      };

      const steps = prev.steps.map((item) =>
        String(item.id) === String(stepId) ? patchApiStepFromEditor(step, nextEditorStep) : item
      );

      let transitions = removeButtonTransition(
        prev.transitions,
        step.step_order,
        option.payload || option.label
      );

      const updatedEditorStep = mapFlowToEditorSteps({
        steps: steps.filter((item) => String(item.id) === String(stepId)),
        transitions
      })[0];

      if (updatedEditorStep?.options?.length) {
        transitions = syncTransitionTriggers(
          transitions,
          step.step_order,
          updatedEditorStep.options
        );
      }

      return { steps, transitions };
    });
  };

  const handleEdit = () => setIsEditing(true);

  const removeStep = (stepId) => {
    if (!isEditing) return;

    const editorStep = editorSteps.find((step) => String(step.id) === String(stepId));
    const stepLabel = editorStep?.displayKey || 'this step';
    const confirmed = window.confirm(
      `Delete step ${stepLabel}? Transitions pointing to this step will end the flow.`
    );
    if (!confirmed) return;

    try {
      setLocalFlow((prev) => deleteStepFromFlow(prev, stepId));
      toast.success(`Step ${stepLabel} removed. Save to apply changes.`);
    } catch (err) {
      toast.error(err?.message || 'Could not delete this step.');
    }
  };

  const handleOpenAddStepModal = () => {
    setIsEditing(true);
    const isFirst = localFlow.steps.length === 0;
    const reset = resetAddStepFormState(flow?.slug, isFirst);
    setAddStepTab('text_box');
    setAddStepTemplateName(reset.templateName);
    setAddStepMessageText(reset.messageText);
    setAddStepQuestionText(reset.questionText);
    setAddStepQuestionOptions(reset.questionOptions);
    setAddStepFile(reset.selectedFile);
    setAddStepLibraryMedia(reset.selectedLibraryMedia);
    setAddStepMediaUrlPlaceholderKey(reset.mediaUrlPlaceholderKey);
    setAddStepMediaFilenameTemplate(reset.mediaFilenameTemplate);
    setAddStepModalOpen(true);
  };

  const handleCloseAddStepModal = () => {
    if (isAddingStep) return;
    setAddStepModalOpen(false);
    const reset = resetAddStepFormState(flow?.slug, isFirstStepAdd);
    setAddStepTemplateName(reset.templateName);
    setAddStepMessageText(reset.messageText);
    setAddStepQuestionText(reset.questionText);
    setAddStepQuestionOptions(reset.questionOptions);
    setAddStepFile(reset.selectedFile);
    setAddStepLibraryMedia(reset.selectedLibraryMedia);
    setAddStepMediaUrlPlaceholderKey(reset.mediaUrlPlaceholderKey);
    setAddStepMediaFilenameTemplate(reset.mediaFilenameTemplate);
  };

  const handleAddStepFileChange = (file) => {
    setAddStepFile(file);
    if (file) {
      setAddStepLibraryMedia(null);
      setAddStepMediaUrlPlaceholderKey('');
      setAddStepMediaFilenameTemplate('');
    }
  };

  const handleAddStepLibraryMediaChange = (item) => {
    setAddStepLibraryMedia(item);
    if (item) {
      setAddStepFile(null);
      setAddStepMediaUrlPlaceholderKey('');
      setAddStepMediaFilenameTemplate('');
    }
  };

  const handleAddStepMediaUrlPlaceholderKeyChange = (key) => {
    setAddStepMediaUrlPlaceholderKey(key);
    if (key) {
      setAddStepFile(null);
      setAddStepLibraryMedia(null);
    } else {
      setAddStepMediaFilenameTemplate('');
    }
  };

  const handleConfirmAddStep = async () => {
    const isFirst = localFlow.steps.length === 0;
    const validationError = validateAddStepForm(
      addStepTab,
      {
        messageText: addStepMessageText,
        questionText: addStepQuestionText,
        questionOptions: addStepQuestionOptions,
        file: addStepFile,
        libraryMedia: addStepLibraryMedia,
        mediaUrlPlaceholderKey: addStepMediaUrlPlaceholderKey,
        mediaFilenameTemplate: addStepMediaFilenameTemplate,
        templateName: addStepTemplateName
      },
      { isFirstStep: isFirst }
    );
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const sortedSteps = [...localFlow.steps].sort((a, b) => a.step_order - b.step_order);
    const previousStep = sortedSteps[sortedSteps.length - 1] ?? null;
    const stepOrder = sortedSteps.length;

    setIsAddingStep(true);
    try {
      let uploadedMedia = null;
      if (addStepTab === 'attach_files') {
        if (addStepLibraryMedia) {
          if (!addStepLibraryMedia.url) {
            toast.error('Selected library file has no URL. Choose another file.');
            return;
          }
          uploadedMedia = buildMediaConfigFromLibraryItem(addStepLibraryMedia);
        } else if (addStepFile) {
          const uploadedFiles = await uploadFilesToS3([addStepFile]);
          const uploaded = uploadedFiles?.[0];
          if (!uploaded?.url) {
            toast.error('File upload failed. Please try again.');
            return;
          }
          uploadedMedia = buildMediaConfigFromUpload(addStepFile, uploaded.url);
        } else if (addStepMediaUrlPlaceholderKey) {
          uploadedMedia = buildMediaConfigFromPlaceholder(
            addStepMediaUrlPlaceholderKey,
            addStepMediaFilenameTemplate
          );
        }
      }

      const newStep = buildStepFromAddModal({
        tab: addStepTab,
        messageText: addStepMessageText,
        questionText: addStepQuestionText,
        questionOptions: addStepQuestionOptions,
        file: addStepFile,
        uploadedMedia,
        stepOrder,
        templateName: addStepTemplateName
      });
      const nextLocalFlow = {
        steps: [...sortedSteps, newStep],
        transitions: appendTransitionsForNewStep(
          localFlow.transitions,
          previousStep,
          stepOrder,
          addStepTab
        )
      };

      setLocalFlow(nextLocalFlow);
      console.log('[WhatsApp flow draft payload]', buildFlowSavePayload(flow, nextLocalFlow));

      toast.success(
        addStepTab === 'attach_files' && addStepFile
          ? 'File uploaded and step added.'
          : 'Step added to the flow.'
      );
      setIsAddingStep(false);
      handleCloseAddStepModal();
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not add step. Please try again.');
    } finally {
      setIsAddingStep(false);
    }
  };

  const handleAddQuestionOption = () => {
    const maxOptions = isFirstStepAdd ? MAX_TEMPLATE_BUTTONS : MAX_QUESTION_OPTIONS;
    setAddStepQuestionOptions((prev) => (prev.length < maxOptions ? [...prev, ''] : prev));
  };

  const handleRemoveQuestionOption = (index) => {
    setAddStepQuestionOptions((prev) => {
      if (isFirstStepAdd) {
        return prev.filter((_, i) => i !== index);
      }
      return prev.length > 1 ? prev.filter((_, i) => i !== index) : prev;
    });
  };

  const handleQuestionOptionChange = (index, value) => {
    setAddStepQuestionOptions((prev) =>
      prev.map((opt, i) => (i === index ? value : opt))
    );
  };

  const handleCancel = () => {
    if (flow) {
      setLocalFlow({
        steps: [...(flow.steps || [])].sort((a, b) => a.step_order - b.step_order),
        transitions: [...(flow.transitions || [])]
      });
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!flowId) {
      toast.error('Missing flow id.');
      return;
    }

    const buttonLabelError = validateFlowButtonLabels(localFlow);
    if (buttonLabelError) {
      toast.error(buttonLabelError);
      return;
    }

    const mediaStepError = validateFlowMediaSteps(localFlow);
    if (mediaStepError) {
      toast.error(mediaStepError);
      return;
    }

    const preparedFlow = prepareLocalFlowForSave(localFlow);
    const payload = buildFlowSavePayload(flow, preparedFlow, placeholders);
    setIsSaving(true);
    try {
      await updateWhatsAppFlow(flowId, payload);
      await queryClient.invalidateQueries({ queryKey: ['whatsappFlow', flowId] });
      setLocalFlow(preparedFlow);
      toast.success('Flow saved successfully.');
      setIsEditing(false);
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not save flow. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitTemplate = async () => {
    if (!flowId) {
      toast.error('Missing flow id.');
      return;
    }
    if (!hasTemplateStep) {
      toast.error('Add the opener template (step 1) before submitting to Meta.');
      return;
    }

    const buttonLabelError = validateFlowButtonLabels(localFlow);
    if (buttonLabelError) {
      toast.error(buttonLabelError);
      return;
    }

    setIsSubmittingTemplate(true);
    try {
      const preparedFlow = prepareLocalFlowForSave(localFlow);
      const savePayload = buildFlowSavePayload(flow, preparedFlow, placeholders);
      await updateWhatsAppFlow(flowId, savePayload);
      await submitWhatsAppFlowTemplate(flowId);
      await queryClient.invalidateQueries({ queryKey: ['whatsappFlow', flowId] });
      setLocalFlow(preparedFlow);
      toast.success('Template submitted to Meta for approval.');
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(
        typeof msg === 'string' ? msg : 'Could not submit template to Meta. Please try again.'
      );
    } finally {
      setIsSubmittingTemplate(false);
    }
  };

  const handleOpenSendModal = () => {
    if (!flow?.project_id) {
      toast.error('This flow has no project. Cannot load leads.');
      return;
    }
    setSelectedLeadIds([]);
    setLeadSearchQuery('');
    setSendModalOpen(true);
  };

  const handleCloseSendModal = () => {
    if (isSendingFlow) return;
    setSendModalOpen(false);
  };

  const toggleSendLeadSelection = (leadId) => {
    const id = Number(leadId);
    if (!Number.isFinite(id)) return;
    setSelectedLeadIds((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id);
      if (prev.length >= MAX_FLOW_SEND_RECIPIENTS) {
        toast.error(`You can send to at most ${MAX_FLOW_SEND_RECIPIENTS} leads at a time.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectAllSendModalLeads = () => {
    const ids = filteredSendModalLeads
      .map((lead) => Number(lead?.id))
      .filter((id) => Number.isFinite(id))
      .slice(0, MAX_FLOW_SEND_RECIPIENTS);
    setSelectedLeadIds(ids);
    if (filteredSendModalLeads.length > MAX_FLOW_SEND_RECIPIENTS) {
      toast(`Only the first ${MAX_FLOW_SEND_RECIPIENTS} leads were selected.`, { icon: 'ℹ️' });
    }
  };

  const clearSendLeadSelection = () => {
    setSelectedLeadIds([]);
  };

  const handleSendFlow = async () => {
    if (isSendingFlow) return;
    if (!flowId) {
      toast.error('Missing flow id.');
      return;
    }
    if (selectedLeadIds.length === 0) {
      toast.error('Select at least one lead.');
      return;
    }
    if (selectedLeadIds.length > MAX_FLOW_SEND_RECIPIENTS) {
      toast.error(`You can send to at most ${MAX_FLOW_SEND_RECIPIENTS} leads at a time.`);
      return;
    }

    const chunks = chunkArray(selectedLeadIds, WA_SEND_CHUNK_SIZE);
    const total = selectedLeadIds.length;
    setIsSendingFlow(true);
    setSendFlowProgress(0);

    let okTotal = 0;
    let failTotal = 0;
    let processed = 0;
    let hardError = null;

    try {
      for (const chunk of chunks) {
        try {
          const response = await sendWhatsAppFlow(flowId, {
            recipient_type: 'lead',
            lead_ids: chunk,
            variables: {},
            skip_template_if_session_open: false
          });
          const { ok, fail } = summarizeChunkResults(response, {
            chunkSize: chunk.length,
            chunkAssumedOk: true
          });
          okTotal += ok;
          failTotal += fail;
        } catch (err) {
          failTotal += chunk.length;
          hardError = err;
        }
        processed += chunk.length;
        setSendFlowProgress(processed);
      }

      const toastMsg = formatBulkSendToast({
        ok: okTotal,
        fail: failTotal,
        total,
        labelSingular: 'lead',
        labelPlural: 'leads'
      });

      if (failTotal === 0) {
        toast.success(toastMsg);
      } else if (okTotal === 0) {
        const apiMsg = hardError?.response?.data?.message ?? hardError?.message ?? null;
        toast.error(apiMsg ? `${toastMsg} ${apiMsg}` : toastMsg);
      } else {
        toast.error(toastMsg);
      }

      setSendModalOpen(false);
      setSelectedLeadIds([]);
      setLeadSearchQuery('');
    } finally {
      setIsSendingFlow(false);
      setSendFlowProgress(0);
    }
  };

  return (
    <>
      <Loader isVisible={isLoading || isSaving || isSubmittingTemplate || isSendingFlow || isAddingStep} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow
            onBack={() => navigate('/builder/marketing/whatsapp-flow')}
            data={{
              name: flowTitle
            }}
          />
        </div>

        {!isError ? (
          <div
            className={`fixed z-[9] flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-3 transition-all duration-300 ease-in-out ${
              collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}
            style={{ top: FLOW_HEADER_OFFSET }}>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <p className="truncate text-sm text-gray-600">{flowDescription}</p>
            {templateApprovalStatus ? (
              <TemplateApprovalBadge status={templateApprovalStatus} />
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaceholdersModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50">
              <Braces className="h-4 w-4" />
              Placeholders
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={isLoading || !editorSteps.length}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                viewMode === 'preview'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}>
              <Eye className="h-4 w-4" />
              Preview
            </button>
            <button
              type="button"
              onClick={handlePlay}
              disabled={isLoading || !editorSteps.length}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                viewMode === 'play'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}>
              <Play className="h-4 w-4 fill-current" />
              Play
            </button>

            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleEdit}
                  disabled={isLoading || !editorSteps.length}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
                {hasTemplateStep &&
                String(templateApprovalStatus || '').toUpperCase() === 'DRAFT' ? (
                  <button
                    type="button"
                    onClick={handleSubmitTemplate}
                    disabled={isLoading || isSubmittingTemplate}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">
                    <Send className="h-4 w-4" />
                    {isSubmittingTemplate ? 'Submitting…' : 'Submit to Meta'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleOpenSendModal}
                  disabled={isLoading || !editorSteps.length || !flow?.project_id}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                  <Users className="h-4 w-4" />
                  Send to leads
                </button>
              </>
            )}
          </div>
          </div>
        ) : null}

        <div
          className="flex min-h-[calc(100vh-var(--flow-top-chrome))] flex-col"
          style={{
            backgroundColor: PAGE_BG,
            marginTop: isError ? FLOW_HEADER_OFFSET : FLOW_TOP_CHROME,
            '--flow-top-chrome': isError ? FLOW_HEADER_OFFSET : FLOW_TOP_CHROME
          }}>
          {isError ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-6 py-12 text-center text-rose-700 text-sm max-w-md">
                Could not load this WhatsApp flow. Please try again.
              </div>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px]">
              <div className="custom-scrollbar min-h-0 overflow-y-auto p-6">
                <div className="mx-auto max-w-3xl">
                  {!isLoading && editorSteps.length === 0 ? (
                    <EmptyFlowCard onAddQuestion={handleOpenAddStepModal} />
                  ) : editorSteps.length > 0 ? (
                    <FlowStepsCard
                      steps={editorSteps}
                      isEditing={isEditing}
                      onPromptChange={updatePrompt}
                      onOptionChange={updateOption}
                      onOptionTransitionChange={updateOptionTransition}
                      onAddOption={addOption}
                      onRemoveOption={removeOption}
                      onRemoveStep={removeStep}
                      onOpenAddStep={handleOpenAddStepModal}
                    />
                  ) : null}
                </div>
              </div>

              <FlowPreviewPhone
                previewMessages={previewMessages}
                interactive={viewMode === 'play'}
                isPlayComplete={isPlayComplete}
                onOptionSelect={handlePlayOptionSelect}
              />
            </div>
          )}
        </div>
      </Layout>

      {placeholdersModalOpen ? (
        <PlaceholdersModal
          placeholders={placeholders}
          isLoading={isPlaceholdersLoading}
          isError={isPlaceholdersError}
          onClose={() => setPlaceholdersModalOpen(false)}
        />
      ) : null}

      {sendModalOpen ? (
        <SendFlowToLeadsModal
          flowName={flowTitle}
          leads={sendModalLeads}
          filteredLeads={filteredSendModalLeads}
          isLoading={isSendLeadsLoading}
          isError={isSendLeadsError}
          isSending={isSendingFlow}
          sendProgress={sendFlowProgress}
          selectedLeadIds={selectedLeadIds}
          searchQuery={leadSearchQuery}
          onClose={handleCloseSendModal}
          onContinue={handleSendFlow}
          onSearchChange={setLeadSearchQuery}
          onToggleLead={toggleSendLeadSelection}
          onSelectAll={selectAllSendModalLeads}
          onClearSelection={clearSendLeadSelection}
        />
      ) : null}

      <AddFlowStepModal
        isOpen={addStepModalOpen}
        isFirstStep={isFirstStepAdd}
        activeTab={addStepTab}
        onTabChange={setAddStepTab}
        onClose={handleCloseAddStepModal}
        templateName={addStepTemplateName}
        onTemplateNameChange={setAddStepTemplateName}
        messageText={addStepMessageText}
        onMessageTextChange={setAddStepMessageText}
        questionText={addStepQuestionText}
        onQuestionTextChange={setAddStepQuestionText}
        questionOptions={addStepQuestionOptions}
        onQuestionOptionChange={handleQuestionOptionChange}
        onAddQuestionOption={handleAddQuestionOption}
        onRemoveQuestionOption={handleRemoveQuestionOption}
        projectId={projectId}
        selectedFile={addStepFile}
        onFileChange={handleAddStepFileChange}
        selectedLibraryMedia={addStepLibraryMedia}
        onLibraryMediaChange={handleAddStepLibraryMediaChange}
        mediaUrlPlaceholderKey={addStepMediaUrlPlaceholderKey}
        onMediaUrlPlaceholderKeyChange={handleAddStepMediaUrlPlaceholderKeyChange}
        mediaFilenameTemplate={addStepMediaFilenameTemplate}
        onMediaFilenameTemplateChange={setAddStepMediaFilenameTemplate}
        libraryItems={campaignMediaItems}
        isLibraryLoading={isCampaignMediaLoading}
        isLibraryError={isCampaignMediaError}
        isConfirming={isAddingStep}
        onConfirm={handleConfirmAddStep}
        placeholders={placeholders}
        isPlaceholdersLoading={isPlaceholdersLoading}
        isPlaceholdersError={isPlaceholdersError}
      />
    </>
  );
};

export default WhatsAppFlowEditor;
